import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import {
  BaseTextField,
  ControlledRadioGroup,
  ControlledTextField,
} from '../../../../shared/components';
import { toTwoDecimals } from '../../../../shared/components/form/TextField/textField.utils';
import {
  FINAL_AMOUNT_SOURCE_OPTIONS,
  QUOTE_AS_PER,
  QUOTE_AS_PER_OPTIONS,
} from '../../../domain/work.constants';

const fieldWrapperSx = {
  ml: 4,
  '& label': { color: 'grey.500' },
  '& > div': {
    gridTemplateColumns: '10rem auto',
    columnGap: 4,
  },
};

function get(obj, path) {
  return path.split('.').reduce((acc, k) => acc?.[k], obj);
}

export default function QuoteAsPerSection({ asPer, calculatedValue, derivedConstructionCost }) {
  const { control, clearErrors } = useFormContext();
  const { errors } = useFormState({ name: 'quote.asPer' });
  const error = get(errors, 'quote.asPer');

  const isLumpsum = asPer === QUOTE_AS_PER.LUMPSUM;
  const isSqFt = asPer === QUOTE_AS_PER.SQFT;
  const isPercentage = asPer === QUOTE_AS_PER.PERCENTAGE;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '10rem auto', columnGap: 2 }}>
      <Typography variant='body1' fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
        1. Quote as Per
      </Typography>
      <Controller
        name='quote.asPer'
        control={control}
        rules={{
          required: 'Please select how the quote should be calculated',
        }}
        render={({ field }) => (
          <div className='column'>
            {QUOTE_AS_PER_OPTIONS.map((opt) => {
              const optionId = `asPer-${opt.value}`;
              const isLumpSumOption = opt.value === QUOTE_AS_PER.LUMPSUM;
              const isSqFtOption = opt.value === QUOTE_AS_PER.SQFT;
              const isPercentageOption = opt.value === QUOTE_AS_PER.PERCENTAGE;

              return (
                <div key={opt.value} className='column gap-1'>
                  <Box className='column gap'>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '10rem auto',
                        columnGap: 8,
                        alignItems: isLumpSumOption ? 'center' : 'start',
                      }}
                    >
                      <div>
                        <Stack
                          component='label'
                          htmlFor={optionId}
                          direction='row'
                          gap={1}
                          alignItems='center'
                          sx={{ cursor: 'pointer', alignSelf: 'start' }}
                        >
                          <Radio
                            id={optionId}
                            name='asPer'
                            size='small'
                            disableRipple
                            checked={field.value === opt.value}
                            onChange={() => {
                              field.onChange(opt.value);
                              if (error) clearErrors('quote.asPer');
                            }}
                            sx={{
                              padding: 0,
                              color: 'grey.500',
                              '&.Mui-checked': { color: 'grey.700' },
                            }}
                          />
                          <Typography
                            variant='body1'
                            sx={{ whiteSpace: 'nowrap', color: 'grey.500' }}
                          >
                            {opt.label}
                          </Typography>
                        </Stack>
                        {isLumpSumOption && <div style={{ minHeight: '20px' }} />}
                      </div>

                      {isLumpSumOption && (
                        <ControlledTextField
                          name='quote.lumpsum.value'
                          id='quoteLumpsumValue'
                          rules={{
                            validate: (v, values) => {
                              if (values.quote?.asPer !== QUOTE_AS_PER.LUMPSUM) return true;
                              if (!v) return 'Lump-sum value is required';
                              if (Number(v) <= 0) return 'Amount must be greater than zero';
                              return true;
                            },
                          }}
                          placeholder='Enter amount'
                          disabled={!isLumpsum}
                          inputMode='numeric'
                          showErrorInline
                          reserveErrorSpace
                          prefix='₹'
                          sx={{ maxWidth: 260, width: '100%' }}
                        />
                      )}
                    </Box>

                    {isSqFtOption && (
                      <>
                        <ControlledTextField
                          name='quote.sqft.rate'
                          id='sqft-rate'
                          rules={{
                            validate: (v, values) => {
                              if (!isSqFt) return true;
                              if (values.quote?.asPer !== QUOTE_AS_PER.SQFT) return true;
                              if (!v) return 'Rate per sq. ft. is required';
                              if (Number(v) <= 0) return 'Rate must be greater than zero';
                              return true;
                            },
                          }}
                          label='Rate/sq.ft.'
                          disabled={!isSqFt}
                          placeholder='0.00'
                          inputMode='decimal'
                          onBlurNormalize={toTwoDecimals}
                          showErrorInline
                          prefix='₹'
                          sx={{ maxWidth: 260, width: '100%' }}
                          wrapperSx={fieldWrapperSx}
                          orientation='row'
                        />

                        <AreaField disabled={!isSqFt} />
                        <FinalAmountDecisionRow
                          calculatedValue={calculatedValue}
                          disabled={!calculatedValue || !isSqFt}
                          sourceName='quote.sqft.finalSource'
                        />
                      </>
                    )}

                    {isPercentageOption && (
                      <>
                        <AreaField disabled={!isPercentage} />
                        <ControlledTextField
                          name='quote.percentage.constructionRate'
                          id='construction-rate'
                          rules={{
                            validate: (v) => {
                              if (!isPercentage) return true;
                              if (Number(v) <= 0) return 'Rate must be greater than zero';
                              return true;
                            },
                          }}
                          label='Construction Rate'
                          placeholder='0.00'
                          disabled={!isPercentage}
                          inputMode='decimal'
                          onBlurNormalize={toTwoDecimals}
                          showErrorInline
                          prefix='₹'
                          sx={{ maxWidth: 260, width: '100%' }}
                          wrapperSx={fieldWrapperSx}
                          orientation='row'
                        />
                        {/*
                          Area x Construction Rate is the construction cost, so once both are in
                          the box shows that figure instead of sitting empty and disabled.
                          It is a read-only field rather than a filled-in form value: the
                          calculation owns the number, and `computeQuoteTotals` derives it
                          the same way when the quotation is saved.
                        */}
                        {derivedConstructionCost ? (
                          <BaseTextField
                            id='construction-cost'
                            value={derivedConstructionCost}
                            onChange={() => {}}
                            label='Construction cost'
                            disabled={!isPercentage}
                            readOnly
                            prefix='₹'
                            helperText='Area × Construction Rate'
                            sx={{ maxWidth: 260, width: '100%' }}
                            wrapperSx={fieldWrapperSx}
                            orientation='row'
                          />
                        ) : (
                          <ControlledTextField
                            id='construction-cost'
                            name='quote.percentage.constructionCostManual'
                            label='Construction cost'
                            placeholder='Enter amount'
                            disabled={!isPercentage}
                            rules={{
                              validate: (v, values) => {
                                if (values.quote?.asPer !== QUOTE_AS_PER.PERCENTAGE) return true;

                                const area = values.quote?.area;
                                const rate = values.quote?.percentage?.constructionRate;

                                const hasDerived = area && rate;

                                if (!hasDerived) {
                                  if (!v) return 'Construction cost is required';
                                  if (Number(v) <= 0)
                                    return 'Construction cost must be greater than zero';
                                }

                                return true;
                              },
                            }}
                            showErrorInline
                            prefix='₹'
                            sx={{ maxWidth: 260, width: '100%' }}
                            wrapperSx={fieldWrapperSx}
                            orientation='row'
                          />
                        )}
                        <ControlledTextField
                          id='percentage'
                          name='quote.percentage.value'
                          label='Percentage'
                          type='number'
                          hideArrow
                          rules={{
                            validate: (v, values) => {
                              if (values.quote?.asPer !== QUOTE_AS_PER.PERCENTAGE) return true;
                              if (!v) return 'Percentage is required';
                              if (Number(v) <= 0) return 'Percentage must be greater than zero';
                              if (Number(v) > 100) return 'Percentage cannot exceed 100';
                              return true;
                            },
                          }}
                          disabled={!isPercentage}
                          showErrorInline
                          suffix='%'
                          sx={{ maxWidth: 260, width: '100%' }}
                          wrapperSx={fieldWrapperSx}
                          orientation='row'
                        />
                        <FinalAmountDecisionRow
                          disabled={!isPercentage || !calculatedValue}
                          calculatedValue={calculatedValue}
                          sourceName='quote.percentage.finalSource'
                        />
                      </>
                    )}
                  </Box>
                  <FormHelperText error>{error?.message}</FormHelperText>
                </div>
              );
            })}

            {/*
              What section B of the quotation PDF says is being quoted. Client-facing, so
              it sits with the basis it describes rather than with the internal Notes box
              at the foot of this step — the letter prints this and never prints those.
              The placeholder follows the chosen basis, because the two read differently:
              a lump sum lists items, a rate describes the floors it covers.
            */}
            <ControlledTextField
              name='quote.scopeDescription'
              id='quote-scope-description'
              label='What this quotation covers (printed on the quotation PDF)'
              placeholder={
                isLumpsum
                  ? 'One item per line, e.g. 1) Renuka mata – 1 Gate'
                  : 'e.g. Resi. & Comm. Bldg.: Double Height Parking/Shop + 10 Upper Floors'
              }
              isMultiline
              rows={isLumpsum ? 3 : 2}
              rules={{
                maxLength: { value: 500, message: 'Cannot exceed 500 characters' },
              }}
              showCharCount
              sx={{ maxWidth: 560, width: '100%' }}
              wrapperSx={{ mt: 2 }}
            />
          </div>
        )}
      />
    </Box>
  );
}

/**
 * The job's area, shown rather than asked for.
 *
 * It is the total area recorded on the Work Details screen — one area per job, not one per
 * quotation raised against it — so this box is read-only and the number arrives through
 * `quote.area`, which `StepAccounting` keeps in step with it. Editing it here would let two
 * quotations on the same building quote different buildings.
 */
function AreaField({ disabled }) {
  const areaRules = {
    validate: (v, values) => {
      if (disabled) return true;
      const asPer = values.quote?.asPer;

      if (asPer === QUOTE_AS_PER.SQFT) {
        if (!v) return 'Set the total area on the Work Details screen first';
        if (Number(v) <= 0) return 'Area must be greater than zero';
      }

      if (v && Number(v) <= 0) {
        return 'Area must be greater than zero';
      }

      return true;
    },
  };

  return (
    <ControlledTextField
      name='quote.area'
      id='quote-area'
      label='Area'
      placeholder='Set on the Work Details screen'
      inputMode='numeric'
      hideArrow
      readOnly
      suffix='sq.ft.'
      disabled={disabled}
      rules={areaRules}
      helperText='From the job’s total area'
      showErrorInline
      sx={{ maxWidth: 260, width: '100%' }}
      wrapperSx={fieldWrapperSx}
      orientation='row'
    />
  );
}

function FinalAmountDecisionRow({ calculatedValue, disabled, sourceName }) {
  return (
    <div className='row gap-3 items-start'>
      <BaseTextField
      id="quote-field-1"
      onChange={() => {}}
        value={calculatedValue ?? ''}
        label='Total'
        disabled={disabled}
        readOnly
        prefix='₹'
        sx={{ maxWidth: 260, width: '100%' }}
        wrapperSx={fieldWrapperSx}
        orientation='row'
      />

      <ControlledRadioGroup
        name={sourceName}
        options={FINAL_AMOUNT_SOURCE_OPTIONS}
        orientation='column'
        disabled={disabled}
        rules={{
          validate: (v) => (!v ? 'Please select how to proceed with the calculated value' : true),
        }}
        sx={{ gap: 1.5 }}
      />
    </div>
  );
}
